import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { ListTable, type Column, type ListState } from '@/components/admin/ListTable';
import { MetaBox } from '@/components/admin/MetaBox';
import { Modal } from '@/components/admin/Modal';
import { Notice } from '@/components/admin/Notice';
import { Pagination } from '@/components/admin/Pagination';
import {
  MANUAL_AUDIENCE_LABELS,
  SCHEDULE_STATUS_LABELS,
  cancelSchedule,
  createManualSchedule,
  fetchNotificationTemplates,
  fetchSchedulePreview,
  fetchSchedules,
  testSendSchedule,
  uploadNotificationAttachment,
  type ManualAudienceKey,
  type ManualMemberStatusFilter,
  type ManualUserActiveFilter,
  type NotificationSchedule,
  type SchedulePreview,
  type ScheduleStatus,
} from '@/features/notifications/api';
import { fetchCohorts, fetchCourses } from '@/features/masters/api';
import { MEMBER_STATUS_LABELS } from '@/features/members/api';
import { ApiError } from '@/lib/api/http';
import { datetimeLocalToIso, formatDateTime, isDatetimeLocalPast, nowDatetimeLocalValue } from '@/lib/datetime';

const USER_ACTIVE_OPTIONS: { value: ManualUserActiveFilter; label: string }[] = [
  { value: 'active', label: '有効なアカウントのみ' },
  { value: 'all', label: '有効・停止中すべて' },
  { value: 'inactive', label: '停止中のアカウントのみ' },
];

const MEMBER_STATUS_FILTER_OPTIONS: { value: ManualMemberStatusFilter; label: string }[] = [
  { value: 'active', label: MEMBER_STATUS_LABELS.active },
  { value: 'graduated', label: MEMBER_STATUS_LABELS.graduated },
  { value: 'withdrawn', label: MEMBER_STATUS_LABELS.withdrawn },
  { value: 'expelled', label: MEMBER_STATUS_LABELS.expelled },
  { value: 'all', label: 'すべて' },
];

const STATUS_FILTERS: { value: ScheduleStatus | ''; label: string }[] = [
  { value: '', label: 'すべて' },
  { value: 'scheduled', label: '配信予定' },
  { value: 'sent', label: '送信済み' },
  { value: 'skipped', label: 'スキップ' },
  { value: 'cancelled', label: 'キャンセル' },
];

const MANUAL_AUDIENCE_OPTIONS: { value: ManualAudienceKey; label: string }[] = [
  { value: 'all', label: MANUAL_AUDIENCE_LABELS.all },
  { value: 'all_students', label: MANUAL_AUDIENCE_LABELS.all_students },
  { value: 'directors', label: MANUAL_AUDIENCE_LABELS.directors },
  { value: 'success', label: MANUAL_AUDIENCE_LABELS.success },
];

/**
 * 手入力の通知を作る（旧・メッセージの一斉配信の統合先 / D-138）。
 * テンプレートから読み込んで直す、または自分で書く。宛先は全員・受講生全員・
 * 理事講師・サクセス担当から選ぶ。作成すると即時（次のtick）で配信される。
 */
function ManualNotificationForm({ onCreated }: { onCreated: () => void }): ReactElement {
  const templates = useQuery({
    queryKey: ['notification-templates'],
    queryFn: fetchNotificationTemplates,
  });
  const cohorts = useQuery({ queryKey: ['cohorts'], queryFn: fetchCohorts });
  const courses = useQuery({ queryKey: ['courses'], queryFn: fetchCourses });
  const [audienceKey, setAudienceKey] = useState<ManualAudienceKey>('all');
  const [cohortId, setCohortId] = useState('');
  const [courseId, setCourseId] = useState('');
  const [memberStatus, setMemberStatus] = useState<ManualMemberStatusFilter>('active');
  const [userActive, setUserActive] = useState<ManualUserActiveFilter>('active');
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [timing, setTiming] = useState<'now' | 'scheduled'>('now');
  const [scheduledFor, setScheduledFor] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // datetime-local は日本時間（Asia/Tokyo）として扱う（D-343）
  const minDateTimeLocal = nowDatetimeLocalValue();
  const scheduledIsPast = scheduledFor ? isDatetimeLocalPast(scheduledFor) : false;

  function applyTemplate(id: string): void {
    setTemplateId(id);
    const template = (templates.data ?? []).find((t) => t.id === id);
    if (!template) return;
    setSubject(template.subject);
    setBody(template.body);
  }

  // {{event_title}} のような差し込みは、この画面では埋められないので残ったままになる
  const unresolved = [...`${subject} ${body}`.matchAll(/\{\{\s*([\w.]+)\s*\}\}/g)].map(
    (m) => m[1],
  );

  async function onSubmit(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      const attachment = file ? await uploadNotificationAttachment(file) : null;
      const payload: Parameters<typeof createManualSchedule>[0] = {
        audience_key: audienceKey,
        subject,
        body,
        scheduled_for:
          timing === 'scheduled' && scheduledFor ? datetimeLocalToIso(scheduledFor) : null,
        attachment_key: attachment?.key ?? null,
        attachment_name: attachment?.filename ?? null,
        attachment_size: attachment?.size ?? null,
      };
      if (audienceKey === 'all_students') {
        if (cohortId) payload.cohort_id = cohortId;
        if (courseId) payload.course_id = courseId;
        if (memberStatus !== 'active') payload.member_status = memberStatus;
        if (userActive !== 'active') payload.user_active = userActive;
      } else if (userActive !== 'active') {
        payload.user_active = userActive;
      }
      await createManualSchedule(payload);
      setSubject('');
      setBody('');
      setTemplateId('');
      setTiming('now');
      setScheduledFor('');
      setFile(null);
      setCohortId('');
      setCourseId('');
      setMemberStatus('active');
      setUserActive('active');
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '通知の作成に失敗しました。');
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    subject.trim().length > 0 &&
    body.trim().length > 0 &&
    (timing === 'now' || (scheduledFor.trim().length > 0 && !scheduledIsPast));

  return (
    <MetaBox title="手入力の通知を新規登録">
      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      <label className="mb-3 block">
        <span className="mb-1.5 block text-wptext-muted">宛先</span>
        <select
          value={audienceKey}
          onChange={(e) => {
            const next = e.target.value as ManualAudienceKey;
            setAudienceKey(next);
            if (next !== 'all_students') {
              setCohortId('');
              setCourseId('');
              setMemberStatus('active');
            }
          }}
          className="rounded-sm border border-wpborder px-3 py-2"
        >
          {MANUAL_AUDIENCE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {audienceKey === 'all_students' && (
        <div className="mb-3 flex flex-wrap items-end gap-3 rounded-sm border border-wpborder bg-white px-4 py-3">
          <label className="block min-w-[8rem]">
            <span className="mb-1.5 block text-wptext-muted">期</span>
            <select
              value={cohortId}
              onChange={(e) => setCohortId(e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">すべて</option>
              {(cohorts.data ?? [])
                .filter((c) => c.is_active || c.id === cohortId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="block min-w-[8rem]">
            <span className="mb-1.5 block text-wptext-muted">コース</span>
            <select
              value={courseId}
              onChange={(e) => setCourseId(e.target.value)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              <option value="">すべて</option>
              {(courses.data ?? [])
                .filter((c) => c.is_active || c.id === courseId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
            </select>
          </label>
          <label className="block min-w-[8rem]">
            <span className="mb-1.5 block text-wptext-muted">在籍状態</span>
            <select
              value={memberStatus}
              onChange={(e) => setMemberStatus(e.target.value as ManualMemberStatusFilter)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              {MEMBER_STATUS_FILTER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block min-w-[10rem]">
            <span className="mb-1.5 block text-wptext-muted">アカウント状態</span>
            <select
              value={userActive}
              onChange={(e) => setUserActive(e.target.value as ManualUserActiveFilter)}
              className="w-full rounded-sm border border-wpborder px-3 py-2"
            >
              {USER_ACTIVE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>
      )}

      {(audienceKey === 'directors' || audienceKey === 'success' || audienceKey === 'all') && (
        <label className="mb-3 block max-w-md">
          <span className="mb-1.5 block text-wptext-muted">アカウント状態</span>
          <select
            value={userActive}
            onChange={(e) => setUserActive(e.target.value as ManualUserActiveFilter)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            {USER_ACTIVE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </label>
      )}

      <fieldset className="mb-3">
        <legend className="mb-1 text-wptext-muted">配信タイミング</legend>
        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="manual-notification-timing"
              checked={timing === 'now'}
              onChange={() => setTiming('now')}
            />
            即時配信
          </label>
          <label className="flex items-center gap-1">
            <input
              type="radio"
              name="manual-notification-timing"
              checked={timing === 'scheduled'}
              onChange={() => setTiming('scheduled')}
            />
            日時を指定
          </label>
          {timing === 'scheduled' && (
            <input
              type="datetime-local"
              value={scheduledFor}
              min={minDateTimeLocal}
              onChange={(e) => setScheduledFor(e.target.value)}
              className="rounded-sm border border-wpborder px-3 py-2"
            />
          )}
        </div>
        {timing === 'scheduled' && scheduledIsPast && (
          <span className="mt-1 block text-wpnotice-error">
            過去の日時は指定できません。現在より後の日時を選んでください。
          </span>
        )}
        {timing === 'now' ? (
          <span className="mt-1 block text-wptext-muted">
            登録するとすぐに（次の配信タイミングで）送られます。
          </span>
        ) : (
          <span className="mt-1 block text-wptext-muted">
            指定した日時になるまでは「配信予定」のまま止まります。
          </span>
        )}
      </fieldset>

      <label className="mb-3 block">
        <span className="mb-1.5 block text-wptext-muted">テンプレートから読み込む（任意）</span>
        <select
          value={templateId}
          onChange={(e) => applyTemplate(e.target.value)}
          className="w-full max-w-xl rounded-sm border border-wpborder px-3 py-2"
        >
          <option value="">使わない（自分で書く）</option>
          {(templates.data ?? [])
            .filter((t) => t.is_active)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
        </select>
        <span className="mt-1 block text-wptext-muted">
          読み込んだ文面はこの画面で自由に直せます。直した内容はこの1回だけに使われ、
          テンプレートそのものは変わりません。
        </span>
      </label>

      {unresolved.length > 0 && (
        <Notice kind="warning" dismissible={false}>
          文面に差し込み欄（{unresolved.map((v) => `{{${v}}}`).join('、')}
          ）が残っています。イベント名や日時など、実際の言葉に書き換えてから送ってください。
        </Notice>
      )}

      <label className="mb-3 block">
        <span className="mb-1.5 block text-wptext-muted">件名</span>
        <input
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          className="w-full max-w-xl rounded-sm border border-wpborder px-3 py-2"
        />
      </label>

      <label className="mb-3 block">
        <span className="mb-1.5 block text-wptext-muted">本文</span>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          className="w-full max-w-xl rounded-sm border border-wpborder px-3 py-2"
        />
      </label>

      <label className="mb-3 block">
        <span className="mb-1.5 block text-wptext-muted">添付ファイル（任意）</span>
        <input
          type="file"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          className="w-full max-w-xl rounded-sm border border-wpborder px-3 py-2"
        />
        {file && (
          <span className="mt-1 block text-wptext-muted">
            {file.name}（{Math.ceil(file.size / 1024)} KB）— メールにのみ付きます
          </span>
        )}
      </label>

      <button
        type="button"
        disabled={submitting || !canSubmit}
        onClick={() => void onSubmit()}
        className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
      >
        {submitting ? '送信中…' : '登録する'}
      </button>
    </MetaBox>
  );
}

/**
 * 事務局の「システム通知の配信（予定・履歴）」（D-130・D-136・D-138）。
 * 通知はシステムが自動で送るものに加え、事務局が手入力で新規登録することもできる
 * （旧・メッセージの一斉配信はここに統合した）。この画面は
 * 「いつ・誰に・どの文面が送られるか（送られたか）」を確認し、必要なら止める場所。
 * イベント単位で見たいときは、イベント詳細の「通知」タブに同じものが出る。
 */
export function NotificationSchedulesPage(): ReactElement {
  const queryClient = useQueryClient();
  // 既定は「これから送るもの」。履歴を見たいときは「送信済み」や「すべて」に切り替える
  const [status, setStatus] = useState<ScheduleStatus | ''>('scheduled');
  const [page, setPage] = useState(1);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<SchedulePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);

  const query = useQuery({
    queryKey: ['notification-schedules', 'all', status, page],
    queryFn: () => (status ? fetchSchedules({ status, page }) : fetchSchedules({ page })),
  });

  let state: ListState = 'ready';
  if (query.isPending) state = 'loading';
  else if (query.isError) {
    state = query.error instanceof ApiError && query.error.status === 403 ? 'forbidden' : 'error';
  }

  async function onPreview(schedule: NotificationSchedule): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      setPreview(await fetchSchedulePreview(schedule.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'プレビューの取得に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onTestSend(schedule: NotificationSchedule): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const result = await testSendSchedule(schedule.id);
      setNotice(`自分宛（${result.sent_to}）にテスト送信しました。受信者には届いていません。`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'テスト送信に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onCancel(schedule: NotificationSchedule): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await cancelSchedule(schedule.id);
      await queryClient.invalidateQueries({ queryKey: ['notification-schedules'] });
      setNotice('この配信を止めました。');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'キャンセルに失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  async function onManualCreated(): Promise<void> {
    setShowManualForm(false);
    setStatus('scheduled');
    setPage(1);
    await queryClient.invalidateQueries({ queryKey: ['notification-schedules'] });
    setNotice('通知を登録しました。次の配信タイミングで送られます。');
  }

  const columns: Column<NotificationSchedule>[] = [
    {
      key: 'scheduled_for',
      header: '配信予定日時',
      render: (s) => formatDateTime(s.scheduled_for),
    },
    {
      key: 'template_name',
      header: '文面',
      primary: true,
      render: (s) => {
        const title = s.template_name ?? s.manual_subject ?? '—';
        return s.attachment_name ? `${title}（添付: ${s.attachment_name}）` : title;
      },
    },
    {
      key: 'source',
      header: '作成方法',
      render: (s) => (s.is_manual ? '手入力' : '自動'),
    },
    {
      key: 'audience',
      header: '宛先',
      render: (s) => {
        if (!s.is_manual) return '—';
        const base =
          MANUAL_AUDIENCE_LABELS[s.audience_key as ManualAudienceKey] ?? s.audience_key;
        return s.audience_filter_summary ? `${base}（${s.audience_filter_summary}）` : base;
      },
    },
    {
      key: 'event',
      header: '対象イベント',
      // イベントの開催日時から逆算する配信はその回へリンクし、カレンダー起点の
      // 常設ルール（毎月10日・毎週金曜）は周期そのものを出す。「イベントに紐づかない配信」
      // では何の配信か伝わらなかった（D-311）
      render: (s) =>
        s.event_id ? (
          <Link to={`/events/${s.event_id}`} className="text-wpaccent hover:underline">
            イベントを開く
          </Link>
        ) : (
          <span className="text-wptext-muted">
            {s.cadence_label ? `${s.cadence_label}の定期配信` : '個別の回に紐づかない配信'}
          </span>
        ),
    },
    { key: 'status', header: '状態', render: (s) => SCHEDULE_STATUS_LABELS[s.status] },
    {
      key: 'sent_at',
      header: '送信日時',
      render: (s) => (s.sent_at ? formatDateTime(s.sent_at) : '—'),
    },
  ];

  return (
    <>
      <PageHeader
        title="システム通知の配信（予定・履歴）"
        description="システムが自動で送る通知の一覧です。これから送られるもの（配信予定）と、送られたもの（送信済み）を状態で切り替えて確認できます。文面の確認・自分宛のテスト送信・配信の停止ができます。全員・受講生全員・理事講師・サクセス担当へまとめて連絡したいときは、下の「手入力の通知」から送れます。"
        actions={
          <button
            type="button"
            onClick={() => setShowManualForm((v) => !v)}
            className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
          >
            {showManualForm ? '閉じる' : '手入力の通知を新規登録'}
          </button>
        }
      />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {notice && <Notice kind="success">{notice}</Notice>}

      {showManualForm && <ManualNotificationForm onCreated={() => void onManualCreated()} />}

      <div className="mb-3 flex flex-wrap items-end gap-2">
        <label className="block">
          <span className="mb-1.5 block text-wptext-muted">状態</span>
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as ScheduleStatus | '');
              setPage(1);
            }}
            className="rounded-sm border border-wpborder px-3 py-2"
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* 一覧の行を押した結果はモーダルで出す（D-094 / D-164） */}
      <Modal open={preview !== null} title="送られる文面" onClose={() => setPreview(null)} wide>
        {preview && (
          <>
            <p className="mb-1 text-wptext-muted">宛先 {preview.recipients.length}名</p>
            {preview.audience_filter_summary && (
              <p className="mb-1 text-wptext-muted">絞り込み: {preview.audience_filter_summary}</p>
            )}
            {preview.attachment_name && (
              <p className="mb-1 text-wptext-muted">添付: {preview.attachment_name}</p>
            )}
            <p className="font-bold">{preview.subject}</p>
            <p className="whitespace-pre-wrap">{preview.body}</p>
          </>
        )}
      </Modal>

      <ListTable<NotificationSchedule>
        caption="配信予定一覧"
        rows={query.data?.items ?? []}
        columns={columns}
        rowKey={(s) => s.id}
        state={state}
        errorMessage={query.error instanceof Error ? query.error.message : undefined}
        onRetry={() => void query.refetch()}
        rowActions={[
          { label: '文面を見る', onSelect: (s: NotificationSchedule) => void onPreview(s) },
          {
            label: '自分宛にテスト送信',
            onSelect: (s: NotificationSchedule) => void onTestSend(s),
          },
          {
            label: 'この配信を止める',
            destructive: true,
            onSelect: (s: NotificationSchedule) => void onCancel(s),
          },
        ]}
        emptyTitle="配信予定はありません"
        emptyDescription="イベントを公開すると、通知プリセットに沿って配信予定が作られます。手入力の通知は上のボタンから登録できます。"
      />

      <Pagination
        page={query.data?.page ?? 1}
        perPage={query.data?.per_page ?? 50}
        total={query.data?.total ?? 0}
        onChange={setPage}
      />

      {busy && <p className="mt-2 text-wptext-muted">処理中…</p>}
    </>
  );
}
